import React, { useState, useEffect } from 'react';
import './CallDemoPage.css';

const CallDemoPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isCalling, setIsCalling] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [callUrl, setCallUrl] = useState(null);
  const [showCallInterface, setShowCallInterface] = useState(false);
  
  // Demo configuration - you can modify these values
  const [demoConfig, setDemoConfig] = useState({
    userId: 'demo-user-123',
    phoneNumber: '+1234567890',
    contactId: 'demo-contact-commerit',
    contactType: 'customer',
    contactName: 'Demo Customer'
  });

  // Check if user is already authenticated (stored in sessionStorage)
  useEffect(() => {
    const isAuth = sessionStorage.getItem('demo-authenticated') === 'true';
    setIsAuthenticated(isAuth);
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    
    // Simple password check - you can change this password
    const correctPassword = 'Commerit1!';
    
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError('');
      sessionStorage.setItem('demo-authenticated', 'true');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setError('');
    sessionStorage.removeItem('demo-authenticated');
  };

  const handleCallButtonClick = async () => {
    setIsCalling(true);
    setCallStatus('Initiating call...');
    setError('');
    setCallUrl(null);
    setShowCallInterface(false);

    try {
      const response = await fetch(`https://amicredit-react-twilio-phone-backup-production.up.railway.app/public/initiate-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: demoConfig.phoneNumber,
          contact_id: demoConfig.contactId,
          contact_type: demoConfig.contactType,
          contact_name: demoConfig.contactName,
          user_id: demoConfig.userId
        })
      });

      const data = await response.json();

      if (data.call_url) {
        setCallStatus('Loading call interface...');
        setCallUrl(data.call_url);
        setShowCallInterface(true);
        setIsCalling(false);
        setCallStatus('Call interface loaded successfully!');
        
        // Clear status after a delay
        setTimeout(() => {
          setCallStatus('');
        }, 3000);
      } else {
        throw new Error(data.error || 'Unknown error occurred');
      }
    } catch (error) {
      setError('Call failed: ' + error.message);
      setIsCalling(false);
      setCallStatus('');
    }
  };

  const handleCloseCallInterface = () => {
    setShowCallInterface(false);
    setCallUrl(null);
    setCallStatus('');
    setError('');
  };

  const handleConfigChange = (field, value) => {
    setDemoConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Listen for messages from iframe to close modal
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received message from iframe:', event.data);
      if (event.data && event.data.type === 'CLOSE_CALL_MODAL') {
        console.log('Closing call interface via iframe message');
        handleCloseCallInterface();
      }
    };

    const handleKeyDown = (event) => {
      // Close on Escape key
      if (event.key === 'Escape' && showCallInterface) {
        console.log('Closing call interface via Escape key');
        handleCloseCallInterface();
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCallInterface]);

  if (!isAuthenticated) {
    return (
      <div className="demo-login-container">
        <div className="demo-login-card">
          <div className="demo-login-header">
            <h1>AMICredit Call Demo</h1>
            <p>Enter the development password to access the demo</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="demo-login-form">
            <div className="form-group">
              <label htmlFor="password">Password:</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" className="login-btn">
              Access Demo
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <div className="demo-header">
        <h1>AMICredit Call Interface Demo</h1>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>

      <div className="demo-content">
        <div className="demo-layout">
          {/* Left Side - Configuration */}
          <div className="demo-left">
            <div className="demo-section">
              <h2>Configuration</h2>
              <div className="config-form">
                <div className="config-group">
                  <label htmlFor="userId">User ID:</label>
                  <input
                    type="text"
                    id="userId"
                    value={demoConfig.userId}
                    onChange={(e) => handleConfigChange('userId', e.target.value)}
                    placeholder="Enter user ID"
                  />
                </div>
                
                <div className="config-group">
                  <label htmlFor="phoneNumber">Recipient Phone Number:</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={demoConfig.phoneNumber}
                    onChange={(e) => handleConfigChange('phoneNumber', e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
                
                <div className="config-group">
                  <label htmlFor="contactId">Contact ID:</label>
                  <input
                    type="text"
                    id="contactId"
                    value={demoConfig.contactId}
                    onChange={(e) => handleConfigChange('contactId', e.target.value)}
                    placeholder="Enter contact ID"
                  />
                </div>
                
                <div className="config-group">
                  <label htmlFor="contactType">Contact Type:</label>
                  <select
                    id="contactType"
                    value={demoConfig.contactType}
                    onChange={(e) => handleConfigChange('contactType', e.target.value)}
                  >
                    <option value="customer">Customer</option>
                    <option value="lead">Lead</option>
                    <option value="prospect">Prospect</option>
                  </select>
                </div>
                
                <div className="config-group">
                  <label htmlFor="contactName">Contact Name:</label>
                  <input
                    type="text"
                    id="contactName"
                    value={demoConfig.contactName}
                    onChange={(e) => handleConfigChange('contactName', e.target.value)}
                    placeholder="Enter contact name"
                  />
                </div>
              </div>
            </div>

            <div className="demo-section">
              <h2>Call Controls</h2>
              <div className="call-demo-container">
                <p>Click the button below to test the call interface:</p>
                <button 
                  className="call-btn"
                  onClick={handleCallButtonClick}
                  disabled={isCalling}
                >
                  {isCalling ? '⏳ ' + callStatus : '📞 Start Call Demo'}
                </button>
                
                {callStatus && !isCalling && (
                  <div className="call-status success">
                    ✅ {callStatus}
                  </div>
                )}
                
                {error && (
                  <div className="call-status error">
                    ❌ {error}
                  </div>
                )}
                
                {isCalling && (
                  <div className="call-status loading">
                    <div className="spinner-small"></div>
                    {callStatus}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Side - Call Interface */}
          <div className="demo-right">
            {showCallInterface && callUrl ? (
              <div className="demo-section call-interface-section">
                <div className="call-interface-header">
                  <h2>Call Interface</h2>
                  <button className="close-call-btn" onClick={handleCloseCallInterface}>
                    ✕ Close
                  </button>
                </div>
                <div className="call-interface-container">
                  <iframe 
                    src={callUrl}
                    className="call-interface-iframe"
                    allow="microphone; camera; autoplay; encrypted-media; fullscreen"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-pointer-lock allow-top-navigation"
                    title="AMICredit Call Interface"
                  />
                </div>
              </div>
            ) : (
              <div className="demo-section call-interface-placeholder">
                <div className="placeholder-content">
                  <div className="placeholder-icon">📞</div>
                  <h3>Call Interface</h3>
                  <p>Configure your settings and click "Start Call Demo" to begin</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDemoPage;
